import { z } from 'zod';

/**
 * The available endpoints of the Search Service.
 * - `search`: Search for directory items from OpenSearch.
 * - `searchDetail`: Same as `search` but for the professional detail pages.
 * - `aggregate`: Get the aggregations for a search.
 * - `searchnav`: Get search suggestions for keywords and categories.
 */
export type SearchServiceEndpoints =
    | 'search'
    | 'searchDetail'
    | 'aggregate'
    | 'searchnav';

/**
 * The request body schema for the Search Service `searchnav` endpoint.
 */
export interface SearchNavRequest {
    search?: string;
    size?: number;
}

/**
 * The available sort values for the Search Service search endpoint.
 */
export type SearchServiceSortOption =
    | 'alphabetical'
    | 'created'
    | 'draft'
    | 'featured'
    | 'highPrice'
    | 'lowPrice'
    | 'new'
    | 'newlyCreated'
    | 'popular'
    | 'trending'
    // | 'unpublished' // We don't use unpublished anymore.
    | 'updated'
    | null;

/**
 * The Zod schema for a filter sort option. This is a superset of the Search
 * Service sort options because some sort options are implemented as filters.
 */
export const FILTER_SORT_OPTION_SCHEMA = z.enum([
    'alphabetical',
    'created',
    'draft',
    'featured',
    'highPrice',
    'lowPrice',
    'new',
    'newlyCreated',
    'popular',
    'trending',
    'updated',
    // Sort options that are actually filters:
    'invalidLink',
    'uncategorised',
]);

/**
 * The available sort options for filters. This can be used to generate a sort
 * value and filters for the Search Service.
 */
export type FilterSortOption = z.infer<typeof FILTER_SORT_OPTION_SCHEMA> | null;

/**
 * The Zod schema for the view projects by filter value.
 */
export const VIEW_PROJECTS_BY_OPTION_SCHEMA = z.enum([
    'all',
    'our-projects',
    'projects-we-tagged-in',
]);

/**
 * The available values for the `viewProjectsBy` filter for the Search Service.
 */
export type SearchServiceViewProjectsByOption = z.infer<
    typeof VIEW_PROJECTS_BY_OPTION_SCHEMA
>;

/**
 * The index to query for the Search Service. Used in the `searchType` request
 * parameter.
 */
export type SearchIndex = 'professional' | 'project' | 'product' | 'blogpost';

/**
 * The page to search for. This affects the visibility filter.
 */
export type VisibilityType = 'search' | 'directory' | 'profile';

/**
 * The request body schema for the Search Service `search` endpoint.
 */
export interface SearchServiceRequest {
    /** The search query. */
    search?: string;
    /** The item fields to return. */
    sourceFilter?: string[];
    /** The index to search. Set as undefined to search all indexes. */
    searchType?: SearchIndex;
    /** The page to search for. This affects the visibility filter. Defaults to 'search'. */
    visibilityType?: VisibilityType;
    /** The filters to apply to the search. */
    filters?: SearchServiceFilters;
    /** The sort option to apply to the search. Set empty to sort by relevance score. */
    sortBy?: SearchServiceSortOption;
    /** The page number. Starts at 0. */
    page?: number;
    /** The maximum number of results to return per page. */
    size?: number;
}

/**
 * The request body schema for the Search Service `aggregate` endpoint.
 */
export interface SearchAggregateRequest {
    /** The search query. */
    search?: string;
    /** The index to search. Set as undefined to search all indexes. */
    searchType?: SearchIndex;
    /** The page to search for. This affects the visibility filter. */
    visibilityType?: VisibilityType;
    /** The filters to apply to the search. */
    filters: SearchServiceFilters;
    /** The aggregations to get from the search. */
    aggregations?: SearchAggregationParams;
}

export interface SearchCompositeAggParams {
    size?: number;
    search?: string;
    order?: 'asc' | 'desc';
    after?: Record<string, unknown>;
}

export interface FacetOptionsAggregationParams {
    facet: string;
    search?: string;
}

/**
 * The aggregration params for the Search Service `aggregate` endpoint.
 */
export interface SearchAggregationParams {
    aggCategories?: SearchCompositeAggParams;
    aggProfessionals?: SearchCompositeAggParams;
    aggProfessionalCategories?: SearchCompositeAggParams;
    aggRegions?: SearchCompositeAggParams;
    aggProductPriceRange?: boolean;
    aggProductItemsCount?: boolean;
    aggProductColors?: boolean;
    aggProductMaterials?: SearchCompositeAggParams;
    aggProductLengthRange?: boolean;
    aggProductWidthRange?: boolean;
    aggProductHeightRange?: boolean;
    aggProductDiameterRange?: boolean;
    aggProductWeightRange?: boolean;
    aggBrands?: SearchCompositeAggParams;
    aggDesigners?: SearchCompositeAggParams;
    aggProjectItemsCount?: boolean;
    aggProfessions?: SearchCompositeAggParams;
    aggProfessionalProjectCountRanges?: boolean;
    aggLibraryRanges?: SearchCompositeAggParams;
    aggBusinessFocuses?: boolean;
    aggAssociations?: SearchCompositeAggParams;
    aggProfessionalItemsCount?: boolean;
    aggFacetOptions?: FacetOptionsAggregationParams[];
    aggDownloads?: SearchCompositeAggParams;
    aggAwards?: boolean;
}

export type CompositeAggKeys = Extract<
    keyof SearchAggregationParams,
    | 'aggProfessionals'
    | 'aggProductMaterials'
    | 'aggProfessions'
    | 'aggRegions'
    | 'aggLibraryRanges'
    | 'aggAssociations'
    | 'aggBrands'
    | 'aggDesigners'
    | 'aggProfessionalCategories'
    | 'aggDownloads'
>;

interface ColorOption {
    name: string;
    color: string;
}

export type BusinessFocusType =
    | 'Unspecified'
    | 'Mixed'
    | 'Residential'
    | 'Commercial';

export interface BusinessFocusOption {
    type: BusinessFocusType;
    docCount: number;
}

/**
 * The schema for the `searchLocation` filter of the Search Service request body.
 */
export interface SearchLocation {
    suburb?: string;
    district?: string;
    city?: string;
    region?: string;
    state?: string;
    country?: string;
    slug?: string;
}

interface Profession {
    URLSegment: string;
    Title: string;
}

interface SearchListItem {
    label: string;
    value: string;
}

interface FacetOption {
    id: string;
    name: string;
}

interface FacetOptionsAggregationResult {
    name: string;
    isGeneral: boolean;
    items: FacetOption[];
    selectedItems?: FacetOption[];
}

export type FacetAggregationResult = Record<
    string,
    FacetOptionsAggregationResult
>;

interface Range {
    min: number;
    max: number;
}

/**
 * The response schema for the Search Service `aggregate` endpoint.
 */
export interface SearchAggregations {
    categoryTree?: {
        articles?: SearchCategory;
        products?: SearchCategory;
        professionals?: SearchCategory;
        projects?: SearchCategory;
    };
    productItemsCounts?: {
        purchasableCount?: number;
        onSaleCount?: number;
        inStockCount?: number;
        deliveryAvailableCount?: number;
        sampleAvailableCount?: number;
    };
    totalCount?: number;

    priceRange?: Range;

    productColors?: ColorOption[];
    productMaterials?: {
        after?: Record<string, unknown>;
        items: string[];
        selectedItems?: string[];
    };

    lengthRange?: Range;
    widthRange?: Range;
    heightRange?: Range;
    diameterRange?: Range;
    weightRange?: Range;

    professionals?: {
        after?: Record<string, unknown>;
        items: SearchProfessional[];
        selectedItems?: SearchProfessional[];
    };

    projectItemsCounts?: {
        hasProductItemCount?: number;
        hasVideoItemCount?: number;
        hasAwardItemCount?: number;
        projectOfTheMonthCount?: number;
    };

    professions?: {
        after?: Record<string, unknown>;
        items: Profession[];
        selectedItems?: Profession[];
    };

    projectCountRanges?: CountRange[];
    libraryRanges?: {
        after?: Record<string, unknown>;
        items: SearchListItem[];
        selectedItems?: SearchListItem[];
    };
    businessFocuses?: BusinessFocusOption[];
    associations?: {
        after?: Record<string, unknown>;
        items: SearchAssociation[];
        selectedItems?: SearchAssociation[];
    };

    professionalItemsCounts?: {
        hasAwardItemCount?: number;
    };

    designers?: {
        after?: Record<string, unknown>;
        items: SearchListItem[];
        selectedItems?: SearchListItem[];
    };

    brands?: {
        after?: Record<string, unknown>;
        items: SearchListItem[];
        selectedItems?: SearchListItem[];
    };

    facetOptions?: FacetAggregationResult;

    professionalCategories?: {
        after?: Record<string, unknown>;
        items: SearchListItem[];
        selectedItems?: SearchListItem[];
    };

    downloads?: {
        after?: Record<string, unknown>;
        items: SearchListItem[];
        selectedItems?: SearchListItem[];
    };
    awards?: {
        after?: Record<string, unknown>;
        items: SearchListItem[];
        selectedItems?: SearchListItem[];
    };
}

export interface FacetOptionsFilterParams {
    facet: string;
    option: string;
}

/**
 * The schema for the filters property of the Search Service request body.
 */
export interface SearchServiceFilters {
    categoryLink?: string;
    country?: 'NZ' | 'AU' | 'US' | 'all';
    professionals?: string[];

    // Product filters.
    isPurchasable?: boolean;
    isOnSale?: boolean;
    priceRange?: [number, number];
    isInStock?: boolean;
    isDeliveryAvailable?: boolean;
    isSampleAvailable?: boolean;
    colors?: string[];
    materials?: string[];
    lengthRange?: [number, number];
    widthRange?: [number, number];
    heightRange?: [number, number];
    diameterRange?: [number, number];
    weightRange?: [number, number];
    brands?: string[];
    designers?: string[];
    libraryRanges?: string[];
    downloads?: string[];
    facetOptions?: FacetOptionsFilterParams[];

    // Project filters.
    hasProducts?: boolean;
    hasVideos?: boolean;
    projectOfTheMonth?: boolean;
    professions?: string[];
    professionalsUsed?: number[];
    professionalCategories?: string[];
    /**
     * The mode to filter the professional's projects by (own projects,
     * projects we're tagged in, all). Professional project page only.
     */
    viewProjectsBy?: SearchServiceViewProjectsByOption;

    // Professional filters.
    projectCount?: number;
    professionalParents?: number[];
    businessFocuses?: BusinessFocusType[];
    associations?: string[];
    excludeProductSuppliers?: boolean;

    hasAwards?: boolean;
    awards?: string[];
    /**
     * Filter projects by the IDs of professionals used in the project.
     * Professional project page only.
     */
    searchLocation?: SearchLocation;

    // Admin filters.
    noCategory?: boolean;
    invalidLink?: boolean;
    disabled?: boolean;
}

type FilterKeys = keyof SearchServiceFilters;

export type CheckboxFilterKeys = Extract<
    FilterKeys,
    | 'isPurchasable'
    | 'isOnSale'
    | 'isInStock'
    | 'isDeliveryAvailable'
    | 'isSampleAvailable'
    | 'hasProducts'
    | 'hasVideos'
    | 'hasAwards'
    | 'projectOfTheMonth'
    | 'invalidLink'
    | 'disabled'
>;

export type CheckboxGroupFilterKeys = Extract<FilterKeys, 'colors' | 'awards'>;

export type RangeFilterKeys = Extract<
    FilterKeys,
    | 'priceRange'
    | 'lengthRange'
    | 'widthRange'
    | 'heightRange'
    | 'diameterRange'
    | 'weightRange'
>;

export type SearchListFilterKeys = Extract<
    FilterKeys,
    | 'professionals'
    | 'materials'
    | 'libraryRanges'
    | 'associations'
    | 'professions'
    | 'brands'
    | 'designers'
    | 'professionalCategories'
    | 'downloads'
    | 'awards'
>;

export interface SearchCategory {
    docCount: number;
    title: string;
    url: string;
    child: Record<string, SearchCategory>;
}

interface SearchProfessional {
    Title: string;
    URLSegment: string;
}

type SearchAssociation = SearchProfessional;

export interface CountRange {
    from: number;
    count: number;
}

/**
 * The response schema for the Search Service `search` endpoint.
 */
export interface SearchResults<T> {
    hitsPerPage: number;
    nbHits: number;
    nbPages: number;
    page: number;
    maxResultWindow: number;
    hits: T[];
}
